/**
 * RiskAssessmentDetailView - Print-Ready A4 Document View
 *
 * Displays risk assessment as a professional maritime document that matches
 * the PDF export exactly. Classification society style (Lloyd's Register / DNV).
 *
 * @module components/RiskAssessmentDetailView
 */

import React, { useState } from 'react';
import {
  Download,
  Edit3,
  Trash2,
  History
} from 'lucide-react';
import { getRiskLevel, ALARP_ZONES, STATUS_OPTIONS } from '../config/moduleConfig';
import RASignatureModal from './RASignatureModal';
import BackButton from '../../../components/tailwind/common/BackButton';

/**
 * Get ALARP zone from score
 * @param {number} score - Risk score (1-25)
 * @returns {Object} ALARP zone object
 */
const getAlarpZone = (score) => {
  if (score >= 20) return ALARP_ZONES.intolerable;
  if (score >= 6) return ALARP_ZONES.alarp;
  return ALARP_ZONES.acceptable;
};

/**
 * Format date for document display
 */
const formatDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Section Header Component - Numbered sections
 */
const SectionHeader = ({ number, title }) => (
  <div className="flex items-baseline gap-3 mb-3 mt-8 first:mt-0">
    <span className="text-lg font-bold text-slate-800">{number}</span>
    <h2 className="text-lg font-bold text-slate-800 uppercase tracking-wide">
      {title}
    </h2>
  </div>
);

/**
 * Risk Matrix Display Component - 5x5 matrix for report
 */
const RiskMatrixDisplay = ({ initialL, initialS, residualL, residualS }) => {
  const getRiskColor = (score) => {
    if (score >= 15) return 'bg-red-500';
    if (score >= 8) return 'bg-orange-500';
    if (score >= 4) return 'bg-yellow-400';
    return 'bg-green-500';
  };

  const cells = [];
  // Build grid: Severity (rows, 5 at top down to 1), Likelihood (cols, 1 left to 5)
  for (let s = 5; s >= 1; s--) {
    for (let l = 1; l <= 5; l++) {
      const score = l * s;
      cells.push({ l, s, score, color: getRiskColor(score) });
    }
  }

  return (
    <div className="border border-gray-300">
      <div className="bg-slate-700 text-white text-xs font-semibold px-2 py-1 text-center">
        RISK MATRIX
      </div>
      <div className="p-1">
        <div className="flex">
          {/* Y-axis label */}
          <div className="w-4 flex flex-col items-center justify-center text-xs text-gray-500">
            <span className="transform -rotate-90 whitespace-nowrap">SEVERITY</span>
          </div>
          <div>
            {/* Y-axis values */}
            <div className="flex">
              <div className="w-4 flex flex-col justify-around text-xs text-gray-500 pr-1">
                {[5, 4, 3, 2, 1].map(n => (
                  <span key={n} className="h-6 flex items-center justify-center">{n}</span>
                ))}
              </div>
              {/* Grid */}
              <div className="grid grid-cols-5 gap-px bg-gray-300">
                {cells.map((cell, i) => (
                  <div
                    key={i}
                    className={`${cell.color} w-6 h-6 flex items-center justify-center text-xs font-bold text-white relative`}
                  >
                    {cell.score}
                    {/* Initial marker (filled circle) */}
                    {cell.l === initialL && cell.s === initialS && (
                      <div className="absolute w-3 h-3 bg-black rounded-full border border-white" style={{ top: '2px', left: '2px' }} />
                    )}
                    {/* Residual marker (hollow circle) */}
                    {cell.l === residualL && cell.s === residualS && !(cell.l === initialL && cell.s === initialS) && (
                      <div className="absolute w-3 h-3 bg-white rounded-full border-2 border-black" style={{ bottom: '2px', right: '2px' }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
            {/* X-axis values */}
            <div className="flex ml-4">
              {[1, 2, 3, 4, 5].map(n => (
                <span key={n} className="w-6 text-center text-xs text-gray-500">{n}</span>
              ))}
            </div>
            {/* X-axis label */}
            <div className="text-center text-xs text-gray-500 mt-1 ml-4">LIKELIHOOD</div>
          </div>
        </div>
      </div>
      {/* Legend */}
      <div className="flex justify-center gap-4 text-xs text-gray-600 px-2 py-1 border-t border-gray-200">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-black rounded-full"></span> Initial
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-white rounded-full border-2 border-black"></span> Residual
        </span>
      </div>
    </div>
  );
};

/**
 * RiskAssessmentDetailView Component - Print-ready A4 document format
 */
const RiskAssessmentDetailView = ({
  assessment,
  vesselName = 'VESSEL NAME',
  vesselConfig = {},
  vesselSettings = {},
  companyName = '',
  onBack,
  onEdit,
  onDelete,
  onExport,
  onUpdateSignature,
  onViewHistory
}) => {
  // Signature modal state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureType, setSignatureType] = useState('assessor');

  // Handler for saving signatures
  const handleSignatureSave = (signatureData, type) => {
    if (onUpdateSignature) {
      const updatedSignatures = {
        ...assessment.signatures,
        [type]: signatureData
      };
      onUpdateSignature(updatedSignatures);
    }
  };

  if (!assessment) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center">
        <p className="text-gray-500">Assessment not found</p>
      </div>
    );
  }

  // Calculate risk metrics
  const initialScore = assessment.initialRiskScore || 0;
  const residualScore = assessment.residualRiskScore || 0;
  const riskReduction = initialScore > 0
    ? Math.round(((initialScore - residualScore) / initialScore) * 100)
    : 0;
  const statusInfo = STATUS_OPTIONS[assessment.status] || STATUS_OPTIONS.draft;
  const alarpZone = getAlarpZone(residualScore);

  // Get first hazard for matrix display (or use assessment-level values)
  const firstHazard = assessment.hazards?.[0] || {};
  const initialL = firstHazard.initialLikelihood || 3;
  const initialS = firstHazard.initialSeverity || 3;
  const residualL = firstHazard.residualLikelihood || 2;
  const residualS = firstHazard.residualSeverity || 2;

  return (
    <div className="min-h-screen bg-gray-200 pb-24 lg:pb-8 print:bg-white print:pb-0">
      {/* Sticky Header with Actions - matches other detail views */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left side - Back and Title */}
            <div className="flex items-center gap-3">
              <BackButton onClick={onBack} />
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-gray-900">Risk Assessment</h1>
              </div>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              {onViewHistory && (
                <button
                  onClick={onViewHistory}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="View History"
                >
                  <History className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={onExport}
                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                title="Download PDF"
              >
                <Download className="h-5 w-5" />
              </button>
              {onDelete && (
                <button
                  onClick={onDelete}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Delete</span>
                </button>
              )}
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 transition-colors"
                >
                  <Edit3 className="h-4 w-4" />
                  <span className="hidden sm:inline">Edit</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* A4 Paper Container */}
      <div className="max-w-4xl mx-auto bg-white shadow-xl print:shadow-none print:max-w-none">
        {/* Document Header Bar */}
        <div className="bg-slate-700 text-white px-4 md:px-8 print:px-8 py-4">
          {/* Line 1: RA Title */}
          <div className="text-lg font-bold tracking-wide">
            {(assessment.title || 'UNTITLED').toUpperCase()} RISK ASSESSMENT
          </div>
          {/* Line 2: Vessel & Company */}
          <div className="text-sm text-slate-300 mt-1">
            {vesselName || 'Unknown Vessel'}
            {companyName && ` | ${companyName}`}
          </div>
        </div>

        {/* Document Content */}
        <div className="px-4 md:px-8 print:px-8 py-6">
          {/* Document Title */}
          <div className="pb-4">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              RISK ASSESSMENT REPORT
            </h1>
            <div className="mt-1 h-1 w-32 bg-slate-700"></div>
          </div>

          {/* Section 1.0 - Assessment Details */}
          <SectionHeader number="1.0" title="Assessment Details" />
          <table className="w-full border border-gray-300 text-sm">
            <tbody>
              <tr className="border-b border-gray-300">
                <td className="px-4 py-2 bg-gray-50 font-medium w-1/3 border-r border-gray-300">Title</td>
                <td className="px-4 py-2">{assessment.title || 'Untitled'}</td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="px-4 py-2 bg-gray-50 font-medium border-r border-gray-300">Reference</td>
                <td className="px-4 py-2 font-mono text-xs">{assessment.id}</td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="px-4 py-2 bg-gray-50 font-medium border-r border-gray-300">Type</td>
                <td className="px-4 py-2 capitalize">{assessment.type?.replace(/_/g, ' ') || 'Generic'}</td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="px-4 py-2 bg-gray-50 font-medium border-r border-gray-300">Location</td>
                <td className="px-4 py-2">{assessment.location || '-'}</td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="px-4 py-2 bg-gray-50 font-medium border-r border-gray-300">Status</td>
                <td className="px-4 py-2">
                  <span className={`font-medium ${
                    assessment.status === 'approved' ? 'text-green-700' :
                    assessment.status === 'under_review' ? 'text-blue-700' :
                    'text-gray-700'
                  }`}>
                    {statusInfo.label}
                  </span>
                </td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="px-4 py-2 bg-gray-50 font-medium border-r border-gray-300">Created</td>
                <td className="px-4 py-2">{formatDate(assessment.createdAt) || '-'}</td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="px-4 py-2 bg-gray-50 font-medium border-r border-gray-300">Next Review</td>
                <td className="px-4 py-2">{formatDate(assessment.nextReviewDate) || 'Not scheduled'}</td>
              </tr>
              {assessment.procedureNotes && (
                <tr>
                  <td className="px-4 py-2 bg-gray-50 font-medium border-r border-gray-300">Related Procedures</td>
                  <td className="px-4 py-2 text-gray-700">{assessment.procedureNotes}</td>
                </tr>
              )}
            </tbody>
          </table>

          {assessment.description && (
            <p className="mt-3 text-sm text-gray-700 italic border-l-4 border-gray-200 pl-4">
              {assessment.description}
            </p>
          )}

          {/* Section 2.0 - Risk Summary */}
          <SectionHeader number="2.0" title="Risk Summary" />
          <div className="flex flex-col lg:flex-row print:flex-row gap-6">
            {/* Left: Risk Metrics Table */}
            <div className="flex-1 min-w-0">
              <table className="w-full border border-gray-300 text-sm">
                <tbody>
                  <tr className="border-b border-gray-300">
                    <td className="px-4 py-2 bg-gray-50 font-medium border-r border-gray-300">Initial Risk Score</td>
                    <td className="px-4 py-2">
                      <span className={`font-bold ${
                        initialScore >= 15 ? 'text-red-700' :
                        initialScore >= 8 ? 'text-orange-700' :
                        initialScore >= 4 ? 'text-yellow-700' :
                        'text-green-700'
                      }`}>
                        {initialScore} ({getRiskLevel(initialScore).toUpperCase()})
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-300">
                    <td className="px-4 py-2 bg-gray-50 font-medium border-r border-gray-300">Residual Risk Score</td>
                    <td className="px-4 py-2">
                      <span className={`font-bold ${
                        residualScore >= 15 ? 'text-red-700' :
                        residualScore >= 8 ? 'text-orange-700' :
                        residualScore >= 4 ? 'text-yellow-700' :
                        'text-green-700'
                      }`}>
                        {residualScore} ({getRiskLevel(residualScore).toUpperCase()})
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-300">
                    <td className="px-4 py-2 bg-gray-50 font-medium border-r border-gray-300">Risk Reduction</td>
                    <td className="px-4 py-2 font-bold text-green-700">{riskReduction}%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 bg-gray-50 font-medium border-r border-gray-300">ALARP Status</td>
                    <td className={`px-4 py-2 font-bold ${
                      alarpZone.color === 'red' ? 'text-red-700' :
                      alarpZone.color === 'yellow' ? 'text-amber-700' :
                      'text-green-700'
                    }`}>
                      {alarpZone.label}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Right: 5x5 Risk Matrix */}
            <div className="w-full lg:w-52 print:w-52 flex-shrink-0 flex justify-center lg:justify-start print:justify-start">
              <RiskMatrixDisplay
                initialL={initialL}
                initialS={initialS}
                residualL={residualL}
                residualS={residualS}
              />
            </div>
          </div>

          {/* Section 3.0 - Hazard Register */}
          <SectionHeader number="3.0" title="Hazard Register" />
          {assessment.hazards?.length > 0 ? (
            <div className="overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0 print:mx-0 print:px-0 print:overflow-visible">
            <table className="w-full border border-gray-300 text-sm min-w-[500px] md:min-w-[700px] print:min-w-0">
              <thead>
                <tr className="bg-slate-700 text-white">
                  <th className="px-2 py-2 text-left font-semibold border-r border-slate-600 w-10">No.</th>
                  <th className="px-2 py-2 text-left font-semibold border-r border-slate-600 min-w-[100px]">Hazard</th>
                  <th className="px-2 py-2 text-left font-semibold border-r border-slate-600 w-16">Initial</th>
                  <th className="px-2 py-2 text-left font-semibold border-r border-slate-600 min-w-[150px]">Control Measures</th>
                  <th className="px-2 py-2 text-left font-semibold border-r border-slate-600 w-16">Residual</th>
                  <th className="px-2 py-2 text-left font-semibold w-20">ALARP</th>
                </tr>
              </thead>
              <tbody>
                {assessment.hazards.map((hazard, index) => {
                  const initScore = (hazard.initialLikelihood || 1) * (hazard.initialSeverity || 1);
                  const resScore = (hazard.residualLikelihood || 1) * (hazard.residualSeverity || 1);
                  const hazardAlarp = getAlarpZone(resScore);

                  return (
                    <tr key={hazard.id || index} className="border-b border-gray-300">
                      <td className="px-2 py-2 border-r border-gray-300 text-center font-medium align-top">
                        {index + 1}
                      </td>
                      <td className="px-2 py-2 border-r border-gray-300 align-top">
                        <div className="font-medium text-sm break-words">{hazard.hazardDescription || 'Unnamed'}</div>
                        {hazard.whoAffected && (
                          <div className="text-xs text-gray-500 mt-1">
                            Who affected: {hazard.whoAffected}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 border-r border-gray-300 text-center align-top">
                        <span className={`inline-block px-1 py-0.5 rounded text-xs font-bold whitespace-nowrap ${
                          initScore >= 15 ? 'bg-red-100 text-red-800' :
                          initScore >= 8 ? 'bg-orange-100 text-orange-800' :
                          initScore >= 4 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {hazard.initialLikelihood}×{hazard.initialSeverity}={initScore}
                        </span>
                      </td>
                      <td className="px-2 py-2 border-r border-gray-300 text-xs align-top">
                        {/* Existing Controls */}
                        {hazard.existingControls && hazard.existingControls.length > 0 && (
                          <div className="mb-2">
                            <div className="font-semibold text-gray-700 text-xs mb-1">EXISTING:</div>
                            {Array.isArray(hazard.existingControls) ? (
                              hazard.existingControls.map((ctrl, i) => (
                                <div key={i} className="text-gray-600 break-words">• {typeof ctrl === 'string' ? ctrl : ctrl.description}</div>
                              ))
                            ) : (
                              <div className="text-gray-600 break-words">• {hazard.existingControls}</div>
                            )}
                          </div>
                        )}

                        {/* Additional Controls */}
                        {hazard.additionalControls && hazard.additionalControls.length > 0 && (
                          <div>
                            <div className="font-semibold text-cyan-700 text-xs mb-1">ADDITIONAL:</div>
                            {Array.isArray(hazard.additionalControls) ? (
                              hazard.additionalControls.map((ctrl, i) => (
                                <div key={i} className="text-cyan-600 break-words">• {typeof ctrl === 'string' ? ctrl : ctrl.description}</div>
                              ))
                            ) : (
                              <div className="text-cyan-600 break-words">• {hazard.additionalControls}</div>
                            )}
                          </div>
                        )}

                        {/* No controls */}
                        {(!hazard.existingControls || hazard.existingControls.length === 0) &&
                         (!hazard.additionalControls || hazard.additionalControls.length === 0) && (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-2 py-2 border-r border-gray-300 text-center align-top">
                        <span className={`inline-block px-1 py-0.5 rounded text-xs font-bold whitespace-nowrap ${
                          resScore >= 15 ? 'bg-red-100 text-red-800' :
                          resScore >= 8 ? 'bg-orange-100 text-orange-800' :
                          resScore >= 4 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {hazard.residualLikelihood}×{hazard.residualSeverity}={resScore}
                        </span>
                      </td>
                      <td className="px-2 md:px-3 py-2 text-center align-top">
                        <span className={`text-[10px] md:text-xs font-medium ${
                          hazardAlarp.color === 'red' ? 'text-red-700' :
                          hazardAlarp.color === 'yellow' ? 'text-amber-700' :
                          'text-green-700'
                        }`}>
                          {hazardAlarp.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          ) : (
            <p className="text-gray-500 italic border border-gray-300 p-4">No hazards identified</p>
          )}

          {/* Section 4.0 - Regulatory Compliance */}
          <SectionHeader number="4.0" title="Regulatory Compliance" />
          <div className="border border-gray-300 p-4 text-sm">
            {/* Regulatory Framework Header */}
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide">Regulatory Framework</span>
                <div className="font-semibold text-slate-800">Workboat Code Edition 3 (WBC3)</div>
              </div>
              <div className="text-right">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Compliance Status</span>
                <div className="font-semibold text-green-700">Compliant</div>
              </div>
            </div>

            {/* Compliance Checklist */}
            <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-2 mb-4">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 bg-green-500 rounded-full flex-shrink-0"></div>
                <span className="text-gray-700">
                  <span className="font-medium">WBC3 Section 3.14:</span> Risk Assessment of Operations
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 bg-green-500 rounded-full flex-shrink-0"></div>
                <span className="text-gray-700">
                  <span className="font-medium">WBC3 Section 31:</span> Safety Management
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 bg-green-500 rounded-full flex-shrink-0"></div>
                <span className="text-gray-700">
                  <span className="font-medium">WBC3 Appendix 8:</span> SMS Requirements
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 bg-green-500 rounded-full flex-shrink-0"></div>
                <span className="text-gray-700">
                  <span className="font-medium">ALARP:</span> Risks Reduced ALARP
                </span>
              </div>
            </div>

            {/* WBC3 Reference (if task-specific) */}
            {assessment.selectedTask?.wbc3Reference && (
              <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded">
                <span className="text-xs text-slate-500 uppercase tracking-wide">Task-Specific Reference</span>
                <div className="font-medium text-slate-800 mt-1">{assessment.selectedTask.wbc3Reference}</div>
                {assessment.selectedTask?.title && (
                  <div className="text-sm text-slate-600 mt-1">{assessment.selectedTask.title}</div>
                )}
              </div>
            )}

            {/* Supporting Guidance */}
            <div className="text-xs text-gray-500 mb-3">
              <span className="font-medium">Supporting Guidance:</span> COSWP Chapter 1 (Risk Assessment Methodology)
            </div>

            {/* Compliance Statement */}
            <p className="text-xs text-gray-600 italic border-t border-gray-200 pt-3">
              This risk assessment has been conducted in accordance with Workboat Code Edition 3 (WBC3)
              Section 3.14 requirements for risk assessment of operations. Risks have been identified
              and reduced to As Low As Reasonably Practicable (ALARP) in line with WBC3 Appendix 8
              Safety Management System requirements.
            </p>
          </div>

          {/* Section 5.0 - Signatures */}
          <SectionHeader number="5.0" title="Signatures" />
          <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4 md:gap-8 print:gap-8 mt-4">
            {/* Assessed By */}
            <div className="border border-gray-300 p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-sm text-slate-700">ASSESSED BY</h4>
                {!assessment.signatures?.assessor && (
                  <button
                    onClick={() => {
                      setSignatureType('assessor');
                      setShowSignatureModal(true);
                    }}
                    className="text-xs px-2 py-1 bg-cyan-600 text-white rounded hover:bg-cyan-700 print:hidden"
                  >
                    Sign
                  </button>
                )}
              </div>

              {assessment.signatures?.assessor ? (
                <>
                  <img
                    src={assessment.signatures.assessor.data}
                    alt="Assessor signature"
                    className="h-12 border border-gray-200 rounded mb-2"
                  />
                  <div className="text-sm">
                    <div><span className="text-gray-500">Name:</span> {assessment.signatures.assessor.name}</div>
                    <div><span className="text-gray-500">Position:</span> {assessment.signatures.assessor.position || '-'}</div>
                    <div><span className="text-gray-500">Date:</span> {formatDate(assessment.signatures.assessor.timestamp)}</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="border-b border-gray-400 h-12 mb-2"></div>
                  <div className="text-sm text-gray-400">
                    <div>Name: _________________</div>
                    <div>Position: _________________</div>
                    <div>Date: _________________</div>
                  </div>
                </>
              )}
            </div>

            {/* Approved By */}
            <div className="border border-gray-300 p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-sm text-slate-700">APPROVED BY</h4>
                {!assessment.signatures?.approver && (
                  <button
                    onClick={() => {
                      setSignatureType('approver');
                      setShowSignatureModal(true);
                    }}
                    className="text-xs px-2 py-1 bg-cyan-600 text-white rounded hover:bg-cyan-700 print:hidden"
                  >
                    Sign
                  </button>
                )}
              </div>

              {assessment.signatures?.approver ? (
                <>
                  <img
                    src={assessment.signatures.approver.data}
                    alt="Approver signature"
                    className="h-12 border border-gray-200 rounded mb-2"
                  />
                  <div className="text-sm">
                    <div><span className="text-gray-500">Name:</span> {assessment.signatures.approver.name}</div>
                    <div><span className="text-gray-500">Position:</span> {assessment.signatures.approver.position || 'Master'}</div>
                    <div><span className="text-gray-500">Date:</span> {formatDate(assessment.signatures.approver.timestamp)}</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="border-b border-gray-400 h-12 mb-2"></div>
                  <div className="text-sm text-gray-400">
                    <div>Name: _________________</div>
                    <div>Position: Master</div>
                    <div>Date: _________________</div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Signature Modal */}
          <RASignatureModal
            isOpen={showSignatureModal}
            onClose={() => setShowSignatureModal(false)}
            onSave={handleSignatureSave}
            signatureType={signatureType}
            existingSignature={assessment.signatures?.[signatureType]}
          />

          {/* Document Footer */}
          <div className="mt-8 pt-4 border-t border-gray-300 flex items-center justify-between text-xs text-gray-500">
            <div>
              Document ID: {assessment.id} | Generated: {new Date().toLocaleDateString('en-GB')}
            </div>
            <div>
              FleetSkipper | Page 1 of 1
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .bg-slate-700 {
            background-color: #0f172a !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .bg-slate-700 {
            background-color: #334155 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .text-white {
            color: white !important;
          }
        }
      `}</style>
    </div>
  );
};

export default RiskAssessmentDetailView;
